import React from 'react';
import { Link } from 'react-router-dom';

const APIPage: React.FC = () => {
    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
            {/* Hero Section */}
            <div className="relative overflow-hidden py-24">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23EF5000%22%20fill-opacity%3D%220.05%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>

                <div className="relative px-4 mx-auto sm:max-w-xl md:max-w-full lg:max-w-screen-xl md:px-24 lg:px-8">
                    <div className="max-w-4xl mx-auto text-center">
                        <h1 className="mb-6 text-5xl font-bold leading-tight text-white sm:text-6xl">
                            Powerful <span className="bg-gradient-to-r from-primary-400 to-orange-400 bg-clip-text text-transparent">API</span>
                        </h1>
                        <p className="max-w-2xl mx-auto mb-10 text-xl text-gray-300 leading-relaxed">
                            Integrate URL shortening into your applications with our comprehensive RESTful API.
                        </p>
                    </div>
                </div>
            </div>

            {/* API Features */}
            <div className="relative py-24 bg-gray-800/30 backdrop-blur-sm">
                <div className="px-4 mx-auto sm:max-w-xl md:max-w-full lg:max-w-screen-xl md:px-24 lg:px-8">

                    {/* Quick Start */}
                    <div className="mb-16">
                        <div className="text-center mb-12">
                            <h2 className="mb-6 text-3xl font-bold text-white sm:text-4xl">
                                Quick Start
                            </h2>
                            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
                                Get started with our API in minutes. Simple authentication and intuitive endpoints.
                            </p>
                        </div>

                        <div className="grid gap-8 lg:grid-cols-2">
                            <div>
                                <h3 className="text-xl font-semibold text-white mb-4">1. Get Your API Key</h3>
                                <div className="bg-gray-900/50 border border-gray-700 rounded-macos-lg p-4 mb-4">
                                    <pre className="text-green-400 text-sm">
                                        {`curl -X POST https://api.rfdigi.me/auth
-H "Content-Type: application/json"
-d '{"email": "your@email.com", "password": "password"}'`}
                                    </pre>
                                </div>
                                <p className="text-gray-400 text-sm">
                                    Authenticate with your account credentials to receive your API key.
                                </p>
                            </div>

                            <div>
                                <h3 className="text-xl font-semibold text-white mb-4">2. Shorten Your First URL</h3>
                                <div className="bg-gray-900/50 border border-gray-700 rounded-macos-lg p-4 mb-4">
                                    <pre className="text-green-400 text-sm">
                                        {`curl -X POST https://api.rfdigi.me/shorten
-H "Authorization: Bearer YOUR_API_KEY"
-H "Content-Type: application/json"
-d '{"url": "https://example.com"}'`}
                                    </pre>
                                </div>
                                <p className="text-gray-400 text-sm">
                                    Create your first short URL with a simple POST request.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* API Endpoints */}
                    <div className="mb-16">
                        <h2 className="mb-12 text-3xl font-bold text-white text-center sm:text-4xl">
                            API Endpoints
                        </h2>

                        <div className="space-y-6">
                            <div className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-6 backdrop-blur-sm">
                                <div className="flex items-center mb-4">
                                    <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium mr-4">POST</span>
                                    <code className="text-white font-mono">/api/shorten</code>
                                </div>
                                <p className="text-gray-300 mb-4">Create a new short URL</p>
                                <div className="bg-gray-900/50 border border-gray-700 rounded-macos p-4">
                                    <code className="text-green-400 text-sm">
                                        {`{
  "url": "https://example.com",
  "custom": "my-link",
  "expires": "2024-12-31"
}`}
                                    </code>
                                </div>
                            </div>

                            <div className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-6 backdrop-blur-sm">
                                <div className="flex items-center mb-4">
                                    <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium mr-4">GET</span>
                                    <code className="text-white font-mono">/api/urls</code>
                                </div>
                                <p className="text-gray-300 mb-4">List all your shortened URLs</p>
                                <div className="bg-gray-900/50 border border-gray-700 rounded-macos p-4">
                                    <code className="text-green-400 text-sm">
                                        {`{
  "urls": [
    {
      "id": "abc123",
      "short_url": "https://rfdigi.me/abc123",
      "original_url": "https://example.com",
      "clicks": 42,
      "created_at": "2024-01-15T10:30:00Z"
    }
  ]
}`}
                                    </code>
                                </div>
                            </div>

                            <div className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-6 backdrop-blur-sm">
                                <div className="flex items-center mb-4">
                                    <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium mr-4">GET</span>
                                    <code className="text-white font-mono">/api/analytics/{`{id}`}</code>
                                </div>
                                <p className="text-gray-300 mb-4">Get detailed analytics for a specific URL</p>
                                <div className="bg-gray-900/50 border border-gray-700 rounded-macos p-4">
                                    <code className="text-green-400 text-sm">
                                        {`{
  "total_clicks": 156,
  "unique_clicks": 89,
  "countries": {"ZA": 45, "US": 28, "GB": 16},
  "devices": {"desktop": 52, "mobile": 38, "tablet": 10}
}`}
                                    </code>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Features Grid */}
                    <div className="mb-16">
                        <h2 className="mb-12 text-3xl font-bold text-white text-center sm:text-4xl">
                            API Features
                        </h2>

                        <div className="grid gap-8 lg:grid-cols-3">
                            <div className="p-6 bg-gray-800/50 border border-gray-700 rounded-macos-xl backdrop-blur-sm">
                                <div className="flex items-center justify-center w-12 h-12 mb-4 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-macos shadow-macos">
                                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 8a6 6 0 01-7.743 5.743L10 14l-1 1-1 1H6v2H2v-4l4.257-4.257A6 6 0 1118 8zm-6-4a1 1 0 100 2 2 2 0 012 2 1 1 0 102 0 4 4 0 00-4-4z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <h3 className="mb-3 text-lg font-semibold text-white">Authentication</h3>
                                <p className="text-gray-300 text-sm">
                                    Secure API key authentication with rate limiting and usage tracking.
                                </p>
                            </div>

                            <div className="p-6 bg-gray-800/50 border border-gray-700 rounded-macos-xl backdrop-blur-sm">
                                <div className="flex items-center justify-center w-12 h-12 mb-4 bg-gradient-to-br from-green-500 to-emerald-500 rounded-macos shadow-macos">
                                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <h3 className="mb-3 text-lg font-semibold text-white">RESTful Design</h3>
                                <p className="text-gray-300 text-sm">
                                    Clean, intuitive REST API following industry standards and best practices.
                                </p>
                            </div>

                            <div className="p-6 bg-gray-800/50 border border-gray-700 rounded-macos-xl backdrop-blur-sm">
                                <div className="flex items-center justify-center w-12 h-12 mb-4 bg-gradient-to-br from-purple-500 to-pink-500 rounded-macos shadow-macos">
                                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <h3 className="mb-3 text-lg font-semibold text-white">Comprehensive Docs</h3>
                                <p className="text-gray-300 text-sm">
                                    Detailed documentation with examples, SDKs, and interactive API explorer.
                                </p>
                            </div>

                            <div className="p-6 bg-gray-800/50 border border-gray-700 rounded-macos-xl backdrop-blur-sm">
                                <div className="flex items-center justify-center w-12 h-12 mb-4 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-macos shadow-macos">
                                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <h3 className="mb-3 text-lg font-semibold text-white">Rate Limiting</h3>
                                <p className="text-gray-300 text-sm">
                                    Fair usage policies with configurable rate limits based on your plan.
                                </p>
                            </div>

                            <div className="p-6 bg-gray-800/50 border border-gray-700 rounded-macos-xl backdrop-blur-sm">
                                <div className="flex items-center justify-center w-12 h-12 mb-4 bg-gradient-to-br from-red-500 to-pink-500 rounded-macos shadow-macos">
                                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <h3 className="mb-3 text-lg font-semibold text-white">Secure & Reliable</h3>
                                <p className="text-gray-300 text-sm">
                                    99.9% uptime SLA with enterprise-grade security and data protection.
                                </p>
                            </div>

                            <div className="p-6 bg-gray-800/50 border border-gray-700 rounded-macos-xl backdrop-blur-sm">
                                <div className="flex items-center justify-center w-12 h-12 mb-4 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-macos shadow-macos">
                                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <h3 className="mb-3 text-lg font-semibold text-white">Webhooks</h3>
                                <p className="text-gray-300 text-sm">
                                    Real-time notifications for URL clicks, analytics updates, and more.
                                </p>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            {/* CTA Section */}
            <div className="relative py-24">
                <div className="px-4 mx-auto text-center sm:max-w-xl md:max-w-full lg:max-w-screen-xl md:px-24 lg:px-8">
                    <div className="max-w-3xl mx-auto">
                        <h2 className="mb-6 text-3xl font-bold text-white sm:text-4xl">
                            Ready to integrate?
                        </h2>
                        <p className="mb-10 text-lg text-gray-300">
                            Start building with our API today. Get your API key and begin shortening URLs programmatically.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link
                                to="/register"
                                className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white transition-all duration-300 bg-gradient-to-r from-primary-500 to-orange-500 rounded-macos-lg hover:from-primary-600 hover:to-orange-600 shadow-macos-lg hover:shadow-macos-xl hover:scale-105"
                            >
                                <span className="relative z-10">Get API Key</span>
                                <svg className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                </svg>
                            </Link>
                            <a
                                href="/docs"
                                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-gray-300 transition-all duration-300 border border-gray-600 rounded-macos-lg hover:text-white hover:border-gray-500 hover:bg-gray-800/50 backdrop-blur-sm"
                            >
                                View Documentation
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default APIPage;